import type { FixIssue, FixIssueCategory } from "~/types";
import type { CountryKey, FixMeta } from "~/types";
import { objectEntries, objectKeys } from "./general";

type CategoryKey = FixIssueCategory['key'];

const ISSUE_CATEGORIES: FixIssueCategory[] = [
  {
    key: 'face_photo',
    label: 'Face Photo',
    meta: {
      imageType: 'face', 
      disabledCountries: ['canada']
    },
    issues: [
      { value: 'photo_is_missing', label: 'Photo is Missing' },
      { value: 'with_glasses', label: 'With Glasses' },
      { value: 'additional_people', label: 'Additional People' },
      { value: 'head_body_not_straight', label: 'Head/Body Not Straight' },
      { value: 'hair_hiding_the_face', label: 'Hair Hiding the Face' },
      { value: 'unnatural_facial_expression', label: 'Unnatural Facial Expression' },
      { value: 'shoulder_line_missing', label: 'Shoulder Line Missing' },
      { value: 'not_white_background', label: 'Not White Background' },
      { value: 'eyes_not_at_camera_level', label: 'Eyes Not at Camera Level' },
      { value: 'shadow_light_on_face', label: 'Shadow/Light on Face' },
      { value: 'with_accessories', label: 'With Accessories' },
      { value: 'face_cut_on_sides_top', label: 'Face Cut on Sides/Top' },
      { value: 'wrong_photo', label: 'Wrong Photo' },
      { value: 'blurred_photo', label: 'Blurred Photo' },
      { value: 'without_shirt', label: 'Without Shirt' },
      { value: 'with_tank_top', label: 'With Tank Top' },
      { value: 'photo_from_passport', label: 'Photo from Passport' },
      { value: 'black_and_white_face', label: 'Black and White' }
    ]
  },
  {
    key: 'passport_photo',
    label: 'Passport Photo',
    meta: {
      imageType: 'passport',
      disabledCountries: ['canada', 'new_zealand']
    },
    issues: [
      { value: 'cut_on_sides', label: 'Cut on Sides' },
      { value: 'code_hidden_at_bottom', label: 'Code Hidden at Bottom' },
      { value: 'passport_missing', label: 'Passport Missing' },
      { value: 'blurred_passport', label: 'Blurred Passport' },
      { value: 'not_straight', label: 'Not Straight' },
      { value: 'black_and_white_pass', label: 'Black and White' },
      { value: 'with_shadow_light', label: 'With Shadow/Light' },
      { value: 'wrong_passport', label: 'Wrong Passport' },
      { value: 'need_2_passport_pages', label: 'Need 2 Passport Pages', meta: { imageType: 'passport_2_pages' }},
      { value: 'suspected_wrong_details', label: 'Suspected Wrong Details - Need Passport Photo', meta: { enabledCountries: ['canada'] }}
    ]
  },
  {
    key: 'business',
    label: 'Business',
    meta: {
      enabledCountries: ['india']
    },
    issues: [
      { value: 'missing_invitation_letter', label: 'Missing Invitation Letter' },
      { value: 'letter_not_in_english', label: 'Letter Not in English' },
      { value: 'missing_business_card', label: 'Missing Business Card' },
      { value: 'card_not_in_english', label: 'Card Not in English' }
    ]
  },
  {
    key: 'passport_expiry',
    label: 'Passport Expiry',
    meta: {
      requiresDocumentRenewal: true
    },
    issues: [
      { value: 'valid_less_than_6_months', label: 'Valid Less Than 6 Months' },
      { value: 'passport_5_years_or_less', label: 'Passport 5 Years or Less', meta: { enabledCountries: ['usa'] }},
      { value: 'passport_not_biometric', label: 'Passport Not Biometric', meta: { enabledCountries: ['usa'] }},
      { value: 'expired_passport', label: 'Expired Passport' },
      { value: 'temporary_passport', label: 'Temporary Passport', meta: { enabledCountries: ['india'], submissionRisk: true, requiresDocumentRenewal: false }}
    ]
  },
  {
    key: 'application_details',
    label: 'Application Details',
    meta: {
      submissionRisk: true
    },
    issues: [
      { value: 'past_rejection', label: 'Past Rejection', meta: { enabledCountries: ['usa'] }},
      { value: 'criminal_record', label: 'Criminal Record' },
      { value: 'born_in_iran', label: 'Born in Iran', meta: { enabledCountries: ['usa'] }},
      { value: 'born_in_iraq', label: 'Born in Iraq', meta: { enabledCountries: ['usa'] }},
      { value: 'visit_in_cuba', label: 'Visit in Cuba', meta: { enabledCountries: ['usa'] }}
    ]
  }
];

function getCategoryMeta(categoryKey: CategoryKey) {
  return ISSUE_CATEGORIES.find(cat => cat.key === categoryKey)?.meta;
}

function getIssueOrCategoryMetaBool(issue: FixIssue, categoryKey: CategoryKey, prop: keyof FixMeta) {
  if (issue.meta && typeof issue.meta[prop] === 'boolean') return issue.meta[prop];
  const catMeta = getCategoryMeta(categoryKey);
  return !!catMeta && !!catMeta[prop];
}

function isSubmissionRisk(issue: FixIssue, categoryKey: CategoryKey) {
  return getIssueOrCategoryMetaBool(issue, categoryKey, 'submissionRisk');
}

function requiresDocumentRenewal(issue: FixIssue, categoryKey: CategoryKey) {
  return getIssueOrCategoryMetaBool(issue, categoryKey, 'requiresDocumentRenewal');
}

function forEachCategoryIssue(
  issuesByCategory: Record<CategoryKey, FixIssue[]>,
  fn: (issue: FixIssue, categoryKey: CategoryKey) => boolean
) {
  return objectEntries(issuesByCategory).some(([categoryKey, issuesArr]) =>
    issuesArr.some(issue => fn(issue, categoryKey))
  );
}

export function hasDocumentRenewalIssues(issues: Record<CategoryKey, FixIssue[]>) {
  return forEachCategoryIssue(issues, (issue, categoryKey) => requiresDocumentRenewal(issue, categoryKey));
}

function filterIssues(
  issuesByCategory: Record<CategoryKey, FixIssue[]>,
  predicate: (issue: FixIssue, categoryKey: CategoryKey) => boolean
): Record<CategoryKey, FixIssue[]> {
  const entries = objectEntries(issuesByCategory)
    .map(([categoryKey, issuesArr]) => [categoryKey, issuesArr.filter(issue => predicate(issue, categoryKey))])
    .filter(([, filtered]) => filtered.length);
  return Object.fromEntries(entries);
}

export function separateIssuesBySubmissionRiskFromGrouped(issues: Record<CategoryKey, FixIssue[]>) {
  const submissionRiskIssues = filterIssues(issues, isSubmissionRisk);
  const regularIssues = filterIssues(issues, (issue, categoryKey) => !isSubmissionRisk(issue, categoryKey));
  return {
    submissionRiskIssues: objectKeys(submissionRiskIssues).length ? submissionRiskIssues : null,
    regularIssues: objectKeys(regularIssues).length ? regularIssues : null
  };
}

function getImageType(issue: FixIssue, category: FixIssueCategory) {
  return (issue.meta && issue.meta.imageType) || (category.meta && category.meta.imageType);
}

function hasImageTypeInAll(issuesByCategory: Record<CategoryKey, FixIssue[]>, type: FixMeta['imageType']) {
  return objectEntries(issuesByCategory).some(([categoryKey, issuesArr]) => {
    const category = ISSUE_CATEGORIES.find(cat => cat.key === categoryKey);
    return category && issuesArr.some(issue => getImageType(issue, category!) === type);
  });
}

export function getImageIssueStatus(issuesByCategory: Record<CategoryKey, FixIssue[]>) {
  return {
    hasPersonalPhotoIssue: hasImageTypeInAll(issuesByCategory, 'face'),
    hasPassportPhotoIssue: hasImageTypeInAll(issuesByCategory, 'passport'),
    hasPassport2PagesPhotoIssue: hasImageTypeInAll(issuesByCategory, 'passport_2_pages')
  };
}

function isCountryEnabled(meta: FixMeta, selectedCountry: CountryKey) {
  if (!meta) return true;
  if (meta.disabledCountries && meta.disabledCountries.includes(selectedCountry)) {
    return false;
  }
  if (meta.enabledCountries) {
    return meta.enabledCountries.includes(selectedCountry);
  }
  return true;
}

function getVisibleIssuesForCategory(category: FixIssueCategory, country: CountryKey) {
  return category.issues.filter(issue => {
    const issueMeta = issue.meta || {};
    const categoryMeta = category.meta || {};
    if (issueMeta.enabledCountries || issueMeta.disabledCountries) {
      return isCountryEnabled(issueMeta, country);
    }
    return isCountryEnabled(categoryMeta, country);
  });
}

export function getIssueCategoriesForCountry(country: CountryKey) {
  return ISSUE_CATEGORIES.map(category => {
    const filteredIssues = getVisibleIssuesForCategory(category, country);
    return filteredIssues.length ? {
      key: category.key,
      label: category.label,
      issues: filteredIssues
    } : null;
  }).filter(Boolean) as FixIssueCategory[];
}

function getIssuesByValues(category: FixIssueCategory, values: FixIssue['value'][]) {
  if (!values || !Array.isArray(values)) return [];
  return category.issues.filter(issue => values.includes(issue.value));
}

export function getFixIssuesByCategory(issues: Record<CategoryKey, FixIssue['value'][]>, categories: FixIssueCategory[]) {
  const result: Record<string, FixIssue[]> = {};
  for (let i = 0; i < categories.length; i++) {
    const category = categories[i];
    result[category.key] = getIssuesByValues(category, issues[category.key]);
  }
  return result;
} 